import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAlert } from 'contexts/AlertContext';
import { MeetingRepository } from 'repository/meetingRepository';
import { ScheduleRepository } from 'repository/scheduleRepository';
import { isNativeApp, syncNativeMeetingSchedule } from 'common/webview';
import { MeetingProgress } from 'components/meeting/MeetingProgress';
import { useTranslation } from 'react-i18next';
import { useSkipIfCreated } from 'hooks/useSkipIfCreated';
import { getRandomMeetingName, getRandomTravelName } from 'common/utils/meetingName';
import {
    AppScreen, Button, ButtonFooter, Calendar, FieldGroup, Section, SectionHeader,
    SwitchList, SwitchRow, TextArea, TextField, toDateKey,
} from 'components/ds';
import styles from './MeetingCreate.module.css';
import { toLocalISOString, toUtcISOString } from 'common/utils/datetime';


const STORAGE_KEY = 'meeting_create_data';

export const MeetingCreateStep2 = () => {
    useSkipIfCreated();
    const navigate = useNavigate();
    const location = useLocation();
    const { showAlert } = useAlert();
    const { t } = useTranslation();
    const wdShort = t('common.weekDaysShort', { returnObjects: true }) as string[];
    const [isCreating, setIsCreating] = useState(false);

    // sessionStorage에서 저장된 데이터 로드
    const loadSavedData = () => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : {};
    };

    const savedData = loadSavedData();

    const isTravel = savedData.meetingType === 'TRAVEL';

    /* 모임명 예시 — 여행이면 지금 계절에 맞는 여행 이름을 하나 고릅니다.
       일반 모임 예시("맛집 탐방")가 여행에도 그대로 나오고 있었습니다.
       여행지는 이 화면 다음(travel-step3)에서 고르므로 목적지를 넣을 수는 없습니다.
       화면이 다시 그려질 때마다 바뀌면 산만해서 진입당 하나로 고정합니다. */
    const namePlaceholder = useMemo(() => {
        if (!isTravel) return t('meetingCreate.meetingNamePlaceholder');
        const pick = getRandomTravelName();
        if (!pick) return t('meetingCreate.meetingNamePlaceholder');
        return t('meetingCreate.meetingNameExamplePrefix', { name: pick });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTravel, t]);

    /* 첫 진입엔 랜덤 모임명을 시드로 채워주고, 이전 단계에서 돌아온 경우엔
       사용자가 입력했던 값을 유지. 사용자는 그대로 두거나 자유롭게 수정 가능.

       여행이면 여행용 풀에서 고릅니다 — 시간대·요일 풀은 당일 모임용이라
       2박 3일 일정에 "아침 한 잔" 이 들어갔습니다. */
    const [meetingNameInput, setMeetingNameInput] = useState(
        savedData.meetingName
        || (isTravel ? getRandomTravelName() : '')
        || getRandomMeetingName()
    );
    const [descriptionInput, setDescriptionInput] = useState(savedData.description || '');

    // 정기 모임 (당일 모임에서만 노출) — 이전 Step1 에서 제공되던 옵션을 여기로 이관
    const [isRegularMeeting, setIsRegularMeeting] = useState<boolean>(savedData.isRegularMeeting || false);
    const [repeatType, setRepeatType] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY'>(savedData.repeatType || 'WEEKLY');
    const [repeatInterval, setRepeatInterval] = useState<number>(savedData.repeatInterval || 1);
    // WEEKLY 용 다중 요일 (MON,WED,FRI)
    const [repeatDaysOfWeek, setRepeatDaysOfWeek] = useState<string[]>(() => {
        const raw = savedData.repeatDaysOfWeek;
        return raw ? String(raw).split(',').filter(Boolean) : [];
    });
    // MONTHLY 용 1~31
    const [repeatDayOfMonth, setRepeatDayOfMonth] = useState<number>(savedData.repeatDayOfMonth || 1);
    // 공통 HH:mm (시작 시간 없을 때 fallback)
    const [repeatTime, setRepeatTime] = useState<string>(savedData.repeatTime || '19:00');

    // 당일 모임: 단일 날짜 + 시간
    const [selectedDate, setSelectedDate] = useState<Date | null>(
        savedData.selectedDate ? new Date(savedData.selectedDate) : new Date()
    );
    const [selectedTime, setSelectedTime] = useState(savedData.selectedTime || '');
    const [selectedEndTime, setSelectedEndTime] = useState(savedData.selectedEndTime || '');
    const [selectedPlace, setSelectedPlace] = useState(savedData.selectedPlace || '');
    const [selectedPlaceInfo, setSelectedPlaceInfo] = useState(savedData.selectedPlaceInfo || null);

    // 여행 모임: 기간 선택 (시작일~종료일)
    const [travelStartDate, setTravelStartDate] = useState<Date | null>(
        savedData.travelStartDate ? new Date(savedData.travelStartDate) : null
    );
    const [travelEndDate, setTravelEndDate] = useState<Date | null>(
        savedData.travelEndDate ? new Date(savedData.travelEndDate) : null
    );
    const [dateSelectMode, setDateSelectMode] = useState<'start' | 'end'>('start');
    const [showCalendar, setShowCalendar] = useState(false);

    // location.state에서 데이터 받기 (TimePicker, PlaceSearch, 또는 MeetingEdit에서 돌아온 경우)
    useEffect(() => {
        if (location.state?.selectedTime) {
            setSelectedTime(location.state.selectedTime);
        }
        if (location.state?.selectedEndTime) {
            setSelectedEndTime(location.state.selectedEndTime);
        }
        if (location.state?.selectedPlace) {
            setSelectedPlace(location.state.selectedPlace);
            setSelectedPlaceInfo(location.state.placeInfo);
        }
        if (location.state?.selectedDate) {
            // ISO 문자열을 Date 객체로 변환
            const date = new Date(location.state.selectedDate);
            if (!isNaN(date.getTime())) {
                setSelectedDate(date);
                // 해당 날짜가 있는 월로 달력 이동
                setCurrentYear(date.getFullYear());
                setCurrentMonth(date.getMonth() + 1);
            }
        }
    }, [location.state]);

    // 데이터 변경 시 sessionStorage에 저장 (기존 데이터 보존)
    useEffect(() => {
        const existingData = loadSavedData();
        const dataToSave = {
            ...existingData,
            meetingName: meetingNameInput,
            description: descriptionInput,
            selectedDate: selectedDate?.toISOString(),
            selectedTime,
            selectedEndTime,
            selectedPlace,
            selectedPlaceInfo,
            // 백엔드 LocalDateTime 은 'Z' 접미사 ISO 를 못 읽어 null 로 떨어뜨림
            // → 타임존 없는 로컬 ISO(yyyy-MM-ddTHH:mm:ss) 로 저장. TravelGatherPlace 가 그대로 전송.
            travelStartDate: travelStartDate ? toLocalISOString(travelStartDate) : undefined,
            travelEndDate: travelEndDate ? toLocalISOString(travelEndDate) : undefined,
            // 당일 모임에서만 정기 모임 값 저장, 여행은 항상 false
            isRegularMeeting: isTravel ? false : isRegularMeeting,
            repeatType: isTravel || !isRegularMeeting ? null : repeatType,
            repeatInterval: isTravel || !isRegularMeeting ? null
                : (repeatType === 'WEEKLY' ? repeatInterval : 1),
            repeatDaysOfWeek: isTravel || !isRegularMeeting || repeatType !== 'WEEKLY'
                ? null
                : (repeatDaysOfWeek.length > 0 ? repeatDaysOfWeek.join(',') : null),
            repeatDayOfMonth: isTravel || !isRegularMeeting || repeatType !== 'MONTHLY'
                ? null : repeatDayOfMonth,
            repeatTime: isTravel || !isRegularMeeting ? null : repeatTime,
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    }, [meetingNameInput, descriptionInput, selectedDate, selectedTime, selectedEndTime, selectedPlace, selectedPlaceInfo, travelStartDate, travelEndDate, isRegularMeeting, repeatType, repeatInterval, repeatDaysOfWeek, repeatDayOfMonth, repeatTime, isTravel]);

    // 시작 시간이 바뀌면 이전에 고른 종료 시간은 초기화 (유효 범위가 달라질 수 있으므로)
    useEffect(() => {
        setSelectedEndTime('');
    }, [selectedTime]);

    // 시간 문자열 → 24시간제 분(minute)
    const timeToMinutes = (timeStr: string): number | null => {
        const m = timeStr.match(/(오전|오후)\s*(\d+):(\d+)/);
        if (!m) return null;
        let h = parseInt(m[2]);
        const mm = parseInt(m[3]);
        if (m[1] === '오후' && h !== 12) h += 12;
        else if (m[1] === '오전' && h === 12) h = 0;
        return h * 60 + mm;
    };

    // 종료 시간 후보 — 시작 시간 + 30분부터, 30분 간격, 자정(24:00)까지
    // "(다음날)" 은 표시하지 않음 — 필요 시 자정 이후는 별도 플로우에서 처리
    const endTimeOptions = React.useMemo<string[]>(() => {
        const startMin = selectedTime ? timeToMinutes(selectedTime) : null;
        if (startMin === null) return [];
        const opts: string[] = [];
        for (let m = startMin + 30; m < 24 * 60; m += 30) {
            const h24 = Math.floor(m / 60);
            const minute = m % 60;
            const period = h24 >= 12 ? '오후' : '오전';
            const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
            opts.push(`${period} ${h12}:${minute.toString().padStart(2, '0')}`);
        }
        return opts;
    }, [selectedTime]);

    // 현재 날짜 기준으로 달력 표시
    const today = new Date();
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);

    // 이전 달로 이동

    // 다음 달로 이동

    // 시간 문자열을 24시간 형식의 시/분으로 변환
    const parseTime = (timeStr: string): { hours: number; minutes: number } | null => {
        const match = timeStr.match(/(오전|오후)\s*(\d+):(\d+)/);
        if (!match) return null;
        let hours = parseInt(match[2]);
        const minutes = parseInt(match[3]);
        if (match[1] === '오후' && hours !== 12) hours += 12;
        else if (match[1] === '오전' && hours === 12) hours = 0;
        return { hours, minutes };
    };

    // 오늘 날짜인지 확인
    const isToday = selectedDate &&
        selectedDate.getDate() === today.getDate() &&
        selectedDate.getMonth() === today.getMonth() &&
        selectedDate.getFullYear() === today.getFullYear();

    // 시간 슬롯 동적 생성 (오늘이면 현재 시간 이후만, 다른 날이면 전체)
    const allTimeSlots = [
        '오전 9:00', '오전 9:30', '오전 10:00', '오전 10:30',
        '오전 11:00', '오전 11:30', '오후 12:00', '오후 12:30',
        '오후 1:00', '오후 1:30', '오후 2:00', '오후 2:30',
        '오후 3:00', '오후 3:30', '오후 4:00', '오후 4:30',
        '오후 5:00', '오후 5:30', '오후 6:00', '오후 6:30',
        '오후 7:00', '오후 7:30', '오후 8:00', '오후 8:30',
        '오후 9:00', '오후 9:30', '오후 10:00',
    ];
    const timeSlots = isToday
        ? allTimeSlots.filter(slot => {
            const parsed = parseTime(slot);
            if (!parsed) return false;
            const now = new Date();
            const cutoff = now.getHours() * 60 + now.getMinutes() + 30;
            return parsed.hours * 60 + parsed.minutes >= cutoff;
        })
        : allTimeSlots;

    // 해당 시간이 과거인지 확인
    const isTimePast = (timeStr: string): boolean => {
        if (!isToday) return false;
        const parsed = parseTime(timeStr);
        if (!parsed) return false;
        const now = new Date();
        return parsed.hours < now.getHours() || (parsed.hours === now.getHours() && parsed.minutes <= now.getMinutes());
    };

    // 선택된 시간이 과거이면 초기화
    useEffect(() => {
        if (selectedTime && isTimePast(selectedTime)) {
            setSelectedTime('');
        }
    }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const isNextEnabled = meetingNameInput.trim().length > 0 && (
        isTravel
            ? travelStartDate !== null && travelEndDate !== null
            : selectedDate !== null && selectedTime !== ''
    );

    const handleNext = () => {
        if (isNextEnabled && !isCreating) {
            // 과거 일시 검증
            if (selectedDate && selectedTime) {
                const parsed = parseTime(selectedTime);
                if (parsed) {
                    const meetingDateTime = new Date(selectedDate);
                    meetingDateTime.setHours(parsed.hours, parsed.minutes, 0, 0);
                    if (meetingDateTime <= new Date()) {
                        showAlert({ message: t('meetingCreate.alertFutureTime') });
                        return;
                    }
                }
            }

            // 수정 모드 확인: isEditMode가 명시적으로 true이고, meetingId가 존재해야 함
            const isEditMode = location.state?.isEditMode === true;
            const meetingId = location.state?.meetingId;

            console.log('=== handleNext 호출 ===');
            console.log('isEditMode:', isEditMode);
            console.log('meetingId:', meetingId, 'Type:', typeof meetingId);
            console.log('location.state:', location.state);

            // 수정 모드: isEditMode가 true이고 meetingId가 유효한 문자열일 때만
            if (isEditMode && meetingId && typeof meetingId === 'string') {
                // 수정 모드: location.state에서 전달받은 기존 모임 정보 사용
                const updateData = {
                    meetingName: location.state?.meetingName || '제목 없음',
                    description: location.state?.description || '',
                    category: location.state?.category || 'ETC',
                    maxMembers: location.state?.maxMembers || 10,
                    isPublic: location.state?.isPublic !== false,
                    isRegularMeeting: location.state?.isRegularMeeting || false,
                    repeatType: location.state?.repeatType || null,
                    repeatInterval: location.state?.repeatInterval || null,
                    placeName: selectedPlaceInfo?.name || selectedPlace,
                    placeAddress: selectedPlaceInfo?.address || '',
                    latitude: selectedPlaceInfo?.lat || 0,
                    longitude: selectedPlaceInfo?.lng || 0
                };

                console.log('모임 업데이트 요청 데이터:', updateData);
                console.log('업데이트할 meetingId:', meetingId, 'Type:', typeof meetingId);

                setIsCreating(true);

                // 비동기로 처리
                (async () => {
                    try {
                        // meetingId가 문자열인지 확인
                        const validMeetingId = typeof meetingId === 'string' ? meetingId : String(meetingId);
                        console.log('변환된 meetingId:', validMeetingId);

                        // 1단계: 모임 정보 업데이트 (장소 등)
                        await MeetingRepository.onPutUpdateMeeting(validMeetingId, updateData);
                        console.log('모임 정보 업데이트 완료');

                        // 네이티브 브리지에서 재사용하기 위해 스코프 밖에 보관.
                        // (예전엔 아래 브리지 블록이 같은 시각을 다시 파싱했고, 종료시각도
                        //  서버엔 endDate 를 저장하면서 네이티브엔 +3시간을 보내 어긋났다)
                        let scheduleStartTs: number | null = null;
                        let scheduleEndTs: number | null = null;

                        // 2단계: 날짜/시간이 선택되어 있으면 Schedule 처리 (생성 또는 업데이트)
                        if (selectedDate && selectedTime) {
                            // 날짜와 시간 조합
                            const meetingDate = new Date(selectedDate);
                            console.log('=== 시간 파싱 디버깅 ===');
                            console.log('selectedDate:', selectedDate);
                            console.log('selectedTime:', selectedTime);
                            console.log('meetingDate (초기):', meetingDate.toString());

                            const timeParts = selectedTime.match(/(오전|오후)\s*(\d+):(\d+)/);
                            console.log('timeParts:', timeParts);

                            if (timeParts) {
                                let hours = parseInt(timeParts[2]);
                                const minutes = parseInt(timeParts[3]);
                                console.log('파싱된 hours:', hours, 'minutes:', minutes, 'period:', timeParts[1]);

                                if (timeParts[1] === '오후' && hours !== 12) {
                                    hours += 12;
                                    console.log('오후 변환: hours =', hours);
                                } else if (timeParts[1] === '오전' && hours === 12) {
                                    hours = 0;
                                    console.log('자정 변환: hours =', hours);
                                }

                                meetingDate.setHours(hours, minutes, 0, 0);
                                console.log('setHours 후 meetingDate:', meetingDate.toString());
                                console.log('Local 시간:', meetingDate.toLocaleString('ko-KR'));
                            }

                            // 종료 시간: 사용자가 선택했으면 그 값, 없으면 시작 + 2시간
                            let endDate = new Date(meetingDate);
                            if (selectedEndTime) {
                                const endParts = selectedEndTime.match(/(오전|오후)\s*(\d+):(\d+)/);
                                if (endParts) {
                                    let eh = parseInt(endParts[2]);
                                    const em = parseInt(endParts[3]);
                                    if (endParts[1] === '오후' && eh !== 12) eh += 12;
                                    else if (endParts[1] === '오전' && eh === 12) eh = 0;
                                    endDate = new Date(selectedDate);
                                    endDate.setHours(eh, em, 0, 0);
                                    // 종료가 시작보다 이르면 다음 날로 넘김
                                    if (endDate <= meetingDate) {
                                        endDate.setDate(endDate.getDate() + 1);
                                    }
                                }
                            } else {
                                endDate.setHours(endDate.getHours() + 2);
                            }
                            console.log('endDate:', endDate.toString());

                            // 서버에 저장하는 값과 동일한 시각을 네이티브에도 보낸다
                            scheduleStartTs = meetingDate.getTime();
                            scheduleEndTs = endDate.getTime();

                            // meetingId 유효성 재확인
                            if (!meetingId || typeof meetingId !== 'string') {
                                console.error('Schedule 처리 실패: meetingId가 유효하지 않음', meetingId);
                                throw new Error('모임 ID가 유효하지 않습니다.');
                            }

                            const scheduleData = {
                                meetingId: meetingId,
                                startDatetime: toUtcISOString(meetingDate),
                                endDatetime: toUtcISOString(endDate)
                            };

                            console.log('Schedule 데이터:', scheduleData);
                            console.log('  - meetingId:', scheduleData.meetingId);
                            console.log('  - startDatetime:', scheduleData.startDatetime);
                            console.log('  - endDatetime:', scheduleData.endDatetime);

                            // 기존 scheduleId가 있으면 업데이트, 없으면 생성
                            const existingScheduleId = location.state?.confirmedScheduleId;
                            console.log('기존 scheduleId:', existingScheduleId);

                            if (existingScheduleId) {
                                // Schedule 업데이트
                                console.log('Schedule 업데이트 요청:', existingScheduleId, scheduleData);
                                await ScheduleRepository.onPutUpdateSchedule(existingScheduleId, scheduleData);
                                console.log('Schedule 업데이트 완료');
                            } else {
                                // Schedule 생성 및 확정
                                console.log('Schedule 생성 요청:', scheduleData);
                                const scheduleResponse = await ScheduleRepository.onPostCreateSchedule(scheduleData);
                                console.log('Schedule 생성 응답:', scheduleResponse);

                                if (scheduleResponse?.data) {
                                    // scheduleId 추출: 문자열이면 그대로, 객체면 data 속성에서 추출
                                    const scheduleId = typeof scheduleResponse.data === 'string'
                                        ? scheduleResponse.data
                                        : scheduleResponse.data?.data || scheduleResponse.data?.scheduleId;
                                    console.log('Schedule 확정 요청:', scheduleId, 'Type:', typeof scheduleId);

                                    if (scheduleId && typeof scheduleId === 'string') {
                                        // Schedule 확정
                                        await ScheduleRepository.onPostConfirmSchedule(scheduleId);
                                        console.log('Schedule 확정 완료');

                                        // 모임에 확정된 스케줄 ID 반영
                                        await MeetingRepository.onPutUpdateMeeting(validMeetingId, {
                                            ...updateData,
                                            confirmedScheduleId: scheduleId
                                        });
                                        console.log('모임에 확정 스케줄 ID 반영 완료');
                                    } else {
                                        console.error('scheduleId 추출 실패:', scheduleResponse);
                                    }
                                }
                            }
                        }

                        // 네이티브 환경 + 위치공유 켜져 있던 모임이면 변경된 일정으로 스케줄 재등록.
                        // 시작/종료는 위에서 서버에 저장한 값을 그대로 쓴다(재파싱 금지 — 로케일 의존 제거).
                        // 실제 시작 시각을 넘기면 네이티브가 시작 1시간 전부터 알아서 추적을 켠다.
                        if (isNativeApp()) {
                            const sharing = localStorage.getItem(`locationSharing_${validMeetingId}`) === 'true';
                            if (!sharing) {
                                console.log('[Step2] 위치공유 OFF — 네이티브 스케줄 재등록 생략', validMeetingId);
                            } else if (scheduleStartTs == null || scheduleEndTs == null) {
                                console.warn('[Step2] 일정 시각을 만들지 못해 네이티브 재등록 생략', validMeetingId);
                            } else {
                                const ok = syncNativeMeetingSchedule(validMeetingId, {
                                    startDatetime: scheduleStartTs,
                                    endDatetime: scheduleEndTs,
                                    latitude: selectedPlaceInfo?.lat,
                                    longitude: selectedPlaceInfo?.lng,
                                });
                                if (!ok) {
                                    console.warn('[Step2] 네이티브 스케줄 재등록 실패(이미 종료된 일정 등)', validMeetingId);
                                }
                            }
                        }

                        // 완료 후 상세 페이지로 이동
                        setIsCreating(false);
                        navigate(`/meeting/detail/${validMeetingId}`, { replace: true });
                    } catch (error) {
                        console.error('모임 수정 중 오류 발생:', error);
                        setIsCreating(false);
                        // 에러가 발생해도 상세 페이지로 이동 (최소한 장소는 업데이트 됨)
                        const validMeetingId = typeof meetingId === 'string' ? meetingId : String(meetingId);
                        navigate(`/meeting/detail/${validMeetingId}`, { replace: true });
                    }
                })();
            } else {
                // 생성 모드: API 호출하지 않고 sessionStorage에만 저장 → Step3(장소 탐색)으로 이동
                const allData = loadSavedData();

                if (!allData.meetingName) {
                    showAlert({ message: t('meetingCreate.alertMissingName') });
                    return;
                }

                const meetingTypeData = allData.meetingType;
                const step3Path = meetingTypeData === 'TRAVEL'
                    ? '/meeting/create/travel-step3'
                    : '/meeting/create/step3';

                navigate(step3Path);
            }
        }
    };

    const wd = t('common.weekDaysShort', { returnObjects: true }) as string[];

    /** 여행 기간 한 줄 */
    const travelRangeText = travelStartDate && travelEndDate
        ? (() => {
            const nights = Math.ceil((travelEndDate.getTime() - travelStartDate.getTime()) / 86400000);
            return `${travelStartDate.getMonth() + 1}.${travelStartDate.getDate()}(${wd[travelStartDate.getDay()]})`
                + ` ~ ${travelEndDate.getMonth() + 1}.${travelEndDate.getDate()}(${wd[travelEndDate.getDay()]})`
                + ` · ${t('meeting.nightsDays', { nights, days: nights + 1 })}`;
        })()
        : travelStartDate
            ? `${travelStartDate.getMonth() + 1}.${travelStartDate.getDate()} ~ ${t('meetingCreate.selectArrivalDate')}`
            : t('meetingCreate.selectTravelDatePrompt');

    const goTimePicker = () => navigate('/meeting/time-picker', {
        state: {
            ...location.state,
            selectedDate: selectedDate?.toISOString(),
            selectedTime,
            selectedPlace,
            selectedPlaceInfo,
        },
    });

    return (
        <AppScreen title={t('meetingCreate.meetingInfo')} bar="sub" hasFooter>
            <MeetingProgress currentStep={2} isTravel={isTravel} />

            {/* 모임 정보 */}
            <Section spacing="tight">
                <SectionHeader title={t('meetingCreate.meetingInfo')} />
                <FieldGroup>
                    <TextField
                        id="meeting-name"
                        label={t('meetingCreate.meetingName')}
                        required
                        type="text"
                        placeholder={namePlaceholder}
                        value={meetingNameInput}
                        onChange={(e) => setMeetingNameInput(e.target.value.slice(0, 20))}
                        maxLength={20}
                        showCount
                    />
                    <TextArea
                        id="meeting-desc"
                        label={t('meetingCreate.description')}
                        placeholder={t('meetingCreate.descriptionPlaceholder')}
                        value={descriptionInput}
                        onChange={(e) => setDescriptionInput(e.target.value.slice(0, 200))}
                        maxLength={200}
                        rows={3}
                        showCount
                    />
                </FieldGroup>
            </Section>

            {/* 정기 모임 — 당일 모임에서만 (여행은 기간이 정해진 단건) */}
            {!isTravel && (
                <Section divided>
                    <SwitchList>
                        <SwitchRow
                            label={t('meetingForm.regularMeeting')}
                            desc={t('meetingForm.regularMeetingDesc')}
                            checked={isRegularMeeting}
                            onChange={setIsRegularMeeting}
                        />
                    </SwitchList>

                    {isRegularMeeting && (
                        <div className={styles.repeat}>
                            <SectionHeader title={t('meetingForm.repeatCycle')} />
                            <div className={styles.chips}>
                                {([
                                    { v: 'DAILY', label: t('meetingForm.daily') },
                                    { v: 'WEEKLY', label: t('meetingForm.weekly') },
                                    { v: 'MONTHLY', label: t('meetingForm.monthly') },
                                ] as const).map(o => (
                                    <button
                                        key={o.v}
                                        type="button"
                                        className={repeatType === o.v ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                                        onClick={() => { setRepeatType(o.v); setRepeatInterval(1); }}
                                        aria-pressed={repeatType === o.v}
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>

                            {repeatType === 'WEEKLY' && (
                                <>
                                    <div className={styles.subLabel}>{t('meetingForm.dayOfWeek')}</div>
                                    <div className={styles.chips}>
                                        {(['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const).map((v, i) => {
                                            const on = repeatDaysOfWeek.includes(v);
                                            return (
                                                <button
                                                    key={v}
                                                    type="button"
                                                    className={on ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                                                    onClick={() => setRepeatDaysOfWeek(prev =>
                                                        prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])}
                                                    aria-pressed={on}
                                                >
                                                    {wd[i]}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className={styles.subLabel}>{t('meetingForm.interval')}</div>
                                    <div className={styles.chips}>
                                        {[
                                            { v: 1, label: t('meetingForm.weekly') },
                                            { v: 2, label: t('meetingForm.biweekly') },
                                        ].map(o => (
                                            <button
                                                key={o.v}
                                                type="button"
                                                className={repeatInterval === o.v ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                                                onClick={() => setRepeatInterval(o.v)}
                                                aria-pressed={repeatInterval === o.v}
                                            >
                                                {o.label}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}

                            {repeatType === 'MONTHLY' && (
                                <>
                                    <div className={styles.subLabel}>{t('meetingForm.dayOfMonth')}</div>
                                    <input
                                        type="number"
                                        min={1}
                                        max={31}
                                        className={styles.numberInput}
                                        value={repeatDayOfMonth}
                                        onChange={(e) => setRepeatDayOfMonth(Number(e.target.value))}
                                        aria-label={t('meetingForm.dayOfMonth')}
                                    />
                                </>
                            )}

                            <div className={styles.subLabel}>{t('meetingForm.repeatTime')}</div>
                            <input
                                type="time"
                                className={styles.numberInput}
                                value={repeatTime}
                                onChange={(e) => setRepeatTime(e.target.value)}
                                aria-label={t('meetingForm.repeatTime')}
                            />
                        </div>
                    )}
                </Section>
            )}

            {isTravel ? (
                /* ─── 여행: 기간 ─── */
                <Section divided last>
                    <SectionHeader title={t('meetingCreate.travelPeriod')} />
                    <button
                        type="button"
                        className={styles.picker}
                        onClick={() => setShowCalendar(!showCalendar)}
                        aria-expanded={showCalendar}
                    >
                        <span className={styles.pickerValue}>{travelRangeText}</span>
                    </button>

                    {showCalendar && (
                        <div className={styles.calendarBox}>
                            <div className={styles.chips}>
                                {([
                                    {
                                        v: 'start' as const,
                                        label: `${t('meetingCreate.departure')}${travelStartDate
                                            ? ` ${travelStartDate.getMonth() + 1}.${travelStartDate.getDate()}` : ''}`,
                                    },
                                    {
                                        v: 'end' as const,
                                        label: `${t('meetingCreate.arrival')}${travelEndDate
                                            ? ` ${travelEndDate.getMonth() + 1}.${travelEndDate.getDate()}` : ''}`,
                                    },
                                ]).map(o => (
                                    <button
                                        key={o.v}
                                        type="button"
                                        className={dateSelectMode === o.v ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                                        onClick={() => setDateSelectMode(o.v)}
                                        aria-pressed={dateSelectMode === o.v}
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                            <Calendar
                                value={dateSelectMode === 'start'
                                    ? (travelStartDate ? toDateKey(travelStartDate) : undefined)
                                    : (travelEndDate ? toDateKey(travelEndDate) : undefined)}
                                min={dateSelectMode === 'end' && travelStartDate ? travelStartDate : undefined}
                                onChange={(_key, date) => {
                                    if (dateSelectMode === 'start') {
                                        setTravelStartDate(date);
                                        // 출발이 도착보다 뒤면 도착을 지웁니다
                                        if (travelEndDate && date > travelEndDate) setTravelEndDate(null);
                                        setDateSelectMode('end');
                                    } else {
                                        setTravelEndDate(date);
                                    }
                                }}
                            />
                        </div>
                    )}
                </Section>
            ) : (
                /* ─── 당일: 날짜 + 시간 ─── */
                <>
                    <Section divided>
                        <SectionHeader title={t('meetingCreate.dateSelect')} />
                        <button
                            type="button"
                            className={styles.picker}
                            onClick={() => setShowCalendar(!showCalendar)}
                            aria-expanded={showCalendar}
                        >
                            <span className={styles.pickerValue}>
                                {selectedDate
                                    ? `${selectedDate.getFullYear()}.${selectedDate.getMonth() + 1}.${selectedDate.getDate()} (${wd[selectedDate.getDay()]})`
                                    : t('meetingCreate.selectDatePrompt')}
                            </span>
                        </button>
                        {showCalendar && (
                            <div className={styles.calendarBox}>
                                <Calendar
                                    value={selectedDate ? toDateKey(selectedDate) : undefined}
                                    onChange={(_key, date) => {
                                        setSelectedDate(date);
                                        setShowCalendar(false);
                                    }}
                                />
                            </div>
                        )}
                    </Section>

                    <Section divided>
                        <SectionHeader title={t('meetingCreate.timeSelect')} />
                        <div className={styles.chips}>
                            {timeSlots.map(time => {
                                const past = isTimePast(time);
                                return (
                                    <button
                                        key={time}
                                        type="button"
                                        className={selectedTime === time ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                                        onClick={() => !past && setSelectedTime(time)}
                                        disabled={past}
                                        aria-pressed={selectedTime === time}
                                    >
                                        {time}
                                    </button>
                                );
                            })}
                            {/* 직접 고른 시간 — TimePicker 에서 받아온 값 */}
                            {selectedTime && !timeSlots.includes(selectedTime) && (
                                <button
                                    type="button"
                                    className={`${styles.chip} ${styles.chipOn}`}
                                    onClick={goTimePicker}
                                >
                                    {selectedTime}
                                </button>
                            )}
                            <button type="button" className={styles.chip} onClick={goTimePicker}>
                                {t('meetingCreate.directInput')}
                            </button>
                        </div>
                    </Section>

                    {/* 끝나는 시간 — 시작을 고른 뒤에만 */}
                    {selectedTime && endTimeOptions.length > 0 && (
                        <Section divided last>
                            <SectionHeader
                                title={t('meetingCreate.endTime')}
                                sub={t('meetingCreate.endTimeNote')}
                            />
                            <div className={styles.chips}>
                                {endTimeOptions.map(time => (
                                    <button
                                        key={`end-${time}`}
                                        type="button"
                                        className={selectedEndTime === time ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                                        onClick={() => setSelectedEndTime(time)}
                                        aria-pressed={selectedEndTime === time}
                                    >
                                        {time}
                                    </button>
                                ))}
                            </div>
                        </Section>
                    )}
                </>
            )}

            <ButtonFooter>
                <Button
                    variant="primary"
                    size="lg"
                    block
                    disabled={!isNextEnabled || isCreating}
                    onClick={handleNext}
                >
                    {isCreating
                        ? (location.state?.isEditMode ? t('common.save') : t('meetingCreate.creating'))
                        : (location.state?.isEditMode ? t('common.save') : t('common.next'))}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
